import { useCallback, useEffect, useMemo } from "react";
import {
  extractPersonCpf,
  fetchPersonsByOrganization,
} from "src-next/modules/person-batch-register";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

const EMPTY_PERSONS: fhir5.Person[] = [];

/**
 * Persons da loja (managingOrganization da sessão).
 * Falha isolada: não mistura com o fetch de pacientes com consentimento.
 */
export function useEcommerceOrganizationPersons(
  organizationId?: string,
  revision = 0,
) {
  const load = useCallback((id: string) => fetchPersonsByOrganization(id), []);
  const { run, loading, error, data, status } = useAsyncTask<
    [string],
    fhir5.Person[]
  >({
    task: load,
    initialData: EMPTY_PERSONS,
  });

  useEffect(() => {
    if (!organizationId) return;
    void run(organizationId);
  }, [organizationId, revision, run]);

  const persons = data ?? EMPTY_PERSONS;

  const personByCpf = useMemo(() => {
    const map = new Map<string, fhir5.Person>();
    persons.forEach((person) => {
      const cpf = extractPersonCpf(person);
      if (cpf) map.set(cpf, person);
    });
    return map;
  }, [persons]);

  const personCpfs = useMemo(
    () => new Set(personByCpf.keys()),
    [personByCpf],
  );

  const refetch = useCallback(() => {
    if (!organizationId) return;
    void run(organizationId);
  }, [organizationId, run]);

  return {
    persons,
    personByCpf,
    personCpfs,
    loading,
    error: Boolean(error),
    hasFetchedOnce: status === "success" || status === "error",
    countsReady: status === "success",
    refetch,
  };
}

export type UseEcommerceOrganizationPersonsResult = ReturnType<
  typeof useEcommerceOrganizationPersons
>;

